import React from 'react';
import { useMarketData } from '@/hooks/useMarketData';
import { formatPriceCt } from '@/utils/formatters';
import { RefreshCw, TrendingUp, TrendingDown, Minus, Zap } from 'lucide-react';

const MarketOverview = () => {
  const { marketData, isLoading, error, refetch } = useMarketData();
  const analysis = marketData?.marketAnalysis;
  const trend = error ? undefined : marketData?.trend;
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="pg-title">Marktdaten</h1>
          {error ? (
            <p className="text-sm text-destructive mt-1">{error}</p>
          ) : (
            <p className="text-sm text-muted-foreground mt-1">
              Quelle: {marketData?.source || '–'} &middot; Letztes Update: {marketData?.lastUpdated ? new Date(marketData.lastUpdated).toLocaleTimeString('de-DE') : '–'}
            </p>
          )}
        </div>
        <button onClick={refetch} disabled={isLoading} className="p-2.5 bg-card border border-border rounded-lg text-muted-foreground hover:text-primary hover:border-primary transition-colors">
          <RefreshCw className={isLoading ? 'animate-spin' : ''} size={18} />
        </button>
      </div>

      {/* Hero Price Card */}
      <div className="rounded-xl p-8 text-white relative overflow-hidden" style={{background: 'linear-gradient(135deg, #007D88 0%, #18DDEF 100%)'}}>
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <p className="text-white/80 text-sm font-medium uppercase tracking-wider">EPEX Spot Preis</p>
            <p className="text-5xl font-bold mt-2 tracking-tight">{marketData?.basePrice ? formatPriceCt(marketData.basePrice * 100) : '–'} <span className="text-2xl">ct/kWh</span></p>
            <p className="text-white/80 text-sm mt-2">
              {trend === 'up' ? 'Steigender Trend'
                : trend === 'down' ? 'Fallender Trend'
                : trend === 'stable' ? 'Stabil'
                : 'Kein aktueller Marktwert'}
            </p>
          </div>
          <div className="p-5 rounded-xl bg-white/15 backdrop-blur-sm">
            <Zap size={48} />
          </div>
        </div>
        <div className="absolute -right-12 -bottom-12 w-64 h-64 bg-white/5 rounded-full" />
      </div>

      {analysis && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { label: '7-Tage-Schnitt', value: `${formatPriceCt(analysis.avgPrice * 100)} ct`, color: 'bg-accent text-primary' },
              { label: 'Minimum', value: `${formatPriceCt(analysis.minPrice * 100)} ct`, color: 'bg-[#e6fbf2] text-[#00a864]' },
              { label: 'Maximum', value: `${formatPriceCt(analysis.maxPrice * 100)} ct`, color: 'bg-[#ffe6e6] text-[#cc0000]' },
              { label: 'Volatilitaet', value: `${(analysis.volatility * 100).toFixed(2)} ct`, color: 'bg-[#fff8e0] text-[#b8860b]' },
            ].map(m => (
              <div key={m.label} className="stat-card">
                <p className="stat-label">{m.label}</p>
                <p className="stat-value">{m.value}</p>
                <div className={`stat-icon mt-3 ${m.color}`}><TrendingUp size={18} /></div>
              </div>
            ))}
          </div>

          <div className="hk-card">
            <div className="hk-card-header"><h6>Peak / Off-Peak Analyse</h6></div>
            <div className="hk-card-body">
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 bg-[#fff8e0] rounded-lg">
                  <p className="text-xs font-semibold text-[#b8860b] uppercase tracking-wider">Peak (8-20 Uhr)</p>
                  <p className="text-2xl font-bold text-[#b8860b] mt-2">{formatPriceCt(analysis.avgPeakPrice * 100)}</p>
                  <p className="text-xs text-muted-foreground">ct/kWh</p>
                </div>
                <div className="text-center p-4 bg-accent rounded-lg">
                  <p className="text-xs font-semibold text-primary uppercase tracking-wider">Off-Peak</p>
                  <p className="text-2xl font-bold text-primary mt-2">{formatPriceCt(analysis.avgOffPeakPrice * 100)}</p>
                  <p className="text-xs text-muted-foreground">ct/kWh</p>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Spread</p>
                  <p className="text-2xl font-bold text-foreground mt-2">{formatPriceCt(analysis.peakOffPeakSpread * 100)}</p>
                  <p className="text-xs text-muted-foreground">ct/kWh</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MarketOverview;
